import { NavLink, Outlet } from "react-router-dom";
import { Logo } from "./Logo";
import { useState } from "react";
import { Lock, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const links = [
  { to: "/", label: "Accueil", id: "nav-home-link" },
  { to: "/communiques", label: "Communiqués", id: "nav-news-link" },
  { to: "/essap", label: "ESSAP", id: "nav-essap-link" },
  { to: "/mention-legale", label: "Mention légale", id: "nav-legal-link" },
];

const MobileMenu = () => {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button data-testid="mobile-menu-button" aria-label="Menu" className="lg:hidden p-2 rounded-full hover:bg-emerald-50 text-slate-700"><Menu className="h-6 w-6" /></button>
      </SheetTrigger>
      <SheetContent side="right" aria-describedby={undefined} className="w-72">
        <SheetTitle className="font-display">Menu</SheetTitle>
        <nav className="mt-6 flex flex-col gap-1">
          {[...links, { to: "/admin", label: "Administration", id: "nav-admin-link" }].map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to !== "/admin"} onClick={() => setOpen(false)} data-testid={`${l.id}-mobile`}
              className={({ isActive }) => `px-4 py-3 rounded-xl text-base transition-colors ${isActive ? "bg-emerald-50 text-emerald-800 font-semibold" : "text-slate-700 hover:bg-emerald-50"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
};

const Header = () => (
  <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-emerald-900/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
      <div className="shrink-0"><Logo compact /></div>
      <MobileMenu />
      <nav className="hidden lg:flex items-center gap-1 min-w-0">
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={l.id}
            className={({ isActive }) => `px-3 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${isActive ? "bg-emerald-50 text-emerald-800 font-semibold" : "text-slate-600 hover:text-emerald-800"}`}>
            {l.label}
          </NavLink>
        ))}
        <NavLink to="/admin" data-testid="nav-admin-link" title="Administration" className="inline-flex ml-1 items-center gap-1.5 px-3 py-2 rounded-full text-sm whitespace-nowrap text-slate-500 hover:text-slate-900 transition-colors">
          <Lock className="h-3.5 w-3.5" /> <span className="hidden xl:inline">Administration</span>
        </NavLink>
      </nav>
    </div>
  </header>
);

const Footer = () => (
  <footer className="bg-slate-950 text-slate-400 mt-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-3">
      <div className="md:col-span-2 space-y-4">
        <Logo light />
        <p className="text-sm max-w-md leading-relaxed">Registre officiel des assistants en pharmacie (AP). Vérifiez l'inscription et la validité de la carte verte professionnelle à l'aide du numéro CNAP.</p>
      </div>
      <div className="text-sm space-y-2">
        <div className="text-white font-semibold font-display">Accès rapide</div>
        {links.map((l) => <NavLink key={l.to} to={l.to} className="block hover:text-emerald-300 transition-colors">{l.label}</NavLink>)}
        <NavLink to="/admin" data-testid="footer-admin-link" className="block hover:text-emerald-300 transition-colors">Espace administration</NavLink>
      </div>
    </div>
    <div className="border-t border-white/10 py-5 text-center text-xs">© {new Date().getFullYear()} UAPHARCO. Tous droits réservés.</div>
  </footer>
);

export default function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
